import styled from "@emotion/styled";
import {
  fontSize,
  spacing,
  fontWeight,
  duration,
  text,
} from "@atlas-int/tokens";
import { getLocationLevelFromStringLocationId } from "@atlas-int/data";
import { LocationLevel } from "@atlas-int/data/graphql";
import { useBaseExploreParams } from "../../visualization/paramSchemas";
import { worldGroupDatum } from "@atlas-int/data";
import { useCountriesMetadata } from "@atlas-int/data";
import { uiIcons } from "../../assets/icons";
import {
  countryProfilesUrlBase,
  delayVisibility,
  ExternalLinkImage,
  MenuRow,
  MenuRowIcon,
  MenuRowLabel,
} from "./Components";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { googleAnalyticsCustomEvent } from "../../sharedUtilities/analytics";
import type { GetDataFlagsQuery } from "@atlas-int/data/graphql";

const GET_DATA_FLAGS = gql`
  query GetDataFlags($countryId: Int!) {
    dataFlags(countryId: $countryId) {
      countryId
      inMv
      inCp
    }
  }
`;

const ExploreFurtherRow = styled.div`
  border-top: 1px solid ${text.onDark};
  border-bottom: 1px solid ${text.onDark};
  margin-top: ${spacing[2]};
  margin-bottom: ${spacing[2]};
  padding-top: ${spacing[2]};
  padding-bottom: ${spacing[2]};
  opacity: 0;
  margin-right: 15px;

  animation-name: ${delayVisibility};
  animation-duration: ${duration.fast};
  animation-delay: ${duration.fast};
  animation-timing-function: linear;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
`;

const ExploreFurtherHeader = styled.h2`
  margin: 0px;
  padding: 0px;
  padding-left: ${spacing[4]};
  font-size: ${fontSize.sm};
  text-transform: uppercase;
  color: ${text.onDark};
  font-weight: ${fontWeight.normal};
`;

const ExploreFurtherCalloutLink = styled.a`
  color: ${text.onDark};
  display: block;
  width: fit-content;
`;
const ExploreFurther = () => {
  const [{ exporter, importer }] =
    useBaseExploreParams();

  const countriesState = useCountriesMetadata();
  const countryMetadata = countriesState.status === "success" ? countriesState.data : [];

  let useExporter =
    exporter === worldGroupDatum.groupId ? undefined : exporter;
  let useImporter =
    importer === worldGroupDatum.groupId ? undefined : importer;

  let locationFocus: string | undefined;
  if (useExporter) {
    locationFocus = useExporter;
  } else if (useImporter) {
    locationFocus = useImporter;
  }
  let locationLevel = getLocationLevelFromStringLocationId(locationFocus);

  const numericalLocationId = parseInt(locationFocus?.split("-")?.[1], 10);
  const { data: dataFlagsData, error: dataFlagsError } = useQuery<GetDataFlagsQuery>(GET_DATA_FLAGS, {
    variables: { countryId: numericalLocationId },
    skip: !locationFocus || locationLevel !== LocationLevel.Country,
  });

  if (dataFlagsError) {
    console.error("ExploreFurther: dataFlags query error", dataFlagsError);
  }

  if (locationLevel !== LocationLevel.Country) {
    return null;
  }
  const { inMv, inCp } = dataFlagsData?.dataFlags?.[0] || {};

  let locationMetadatum = countryMetadata.find(
    (country) => country.countryId === locationFocus,
  );

  if (!locationMetadatum) {
    return null;
  }

  const locationName = locationMetadatum.nameShortEn;

  let countryProfilesLink = `${countryProfilesUrlBase}/${numericalLocationId}`;
  let citiesLink = `https://metroverse.hks.harvard.edu/?country=${numericalLocationId}`;

  return (
    <>
      {(inCp || inMv) && (
        <ExploreFurtherRow>
          <ExploreFurtherHeader>Explore Further</ExploreFurtherHeader>
          {inCp && (
            <ExploreFurtherCalloutLink
              href={countryProfilesLink}
              target="_blank"
              onClick={() =>
                googleAnalyticsCustomEvent(
                  "leftnav",
                  "click",
                  "profile",
                  "explore",
                )
              }
            >
              <MenuRow>
                <MenuRowIcon>
                  <ExternalLinkImage src={uiIcons.externalLink} />
                </MenuRowIcon>
                <MenuRowLabel>
                  {locationName}'s <b>country profile</b> in Atlas Country
                  Profiles
                </MenuRowLabel>
              </MenuRow>
            </ExploreFurtherCalloutLink>
          )}
          {inMv && (
            <ExploreFurtherCalloutLink
              onClick={() =>
                googleAnalyticsCustomEvent(
                  "leftnav",
                  "click",
                  "metroverse",
                  "explore",
                )
              }
              href={citiesLink}
              target="_blank"
            >
              <MenuRow>
                <MenuRowIcon>
                  <ExternalLinkImage src={uiIcons.externalLink} />
                </MenuRowIcon>
                <MenuRowLabel>
                  {locationName}'s <b>cities</b> in Metroverse
                </MenuRowLabel>
              </MenuRow>
            </ExploreFurtherCalloutLink>
          )}
        </ExploreFurtherRow>
      )}
    </>
  );
};

export default ExploreFurther;
